import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Skeleton } from '@mui/material';
import { useFetchPublicWantListQuery, publicCollectionApi } from 'store/services/collection/publicCollectionApi';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import {
  useCreateTradeLockMutation,
  useReleaseTradeLockMutation,
  useInitiateTradeMutation,
  SetDetail,
} from 'store/services/trade/tradeApi';
import { useGetPublicProfileQuery } from 'store/services/profile/profileApi';
import { CollectionList } from 'types/interfaces/collectionList';
import { buildCardUrl } from 'utils/cardUrlUtils';
import { SortOrder } from 'types/interfaces/sort';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { Points } from 'components/Points/Points';
import { BalanceWarning } from 'components/BalanceWarning';
import MagicButton from 'components/MagicButton/MagicButton';
import { FoilCardImage } from 'components/FoilCardImage';
import TextInputField from 'components/TextInputField';
import CardsTable from 'components/CardsTable';
import { CardsTableCellsData } from 'components/CardsTable/types';
import { SetIcon } from 'components/SetIcon';
import { AssetUrls } from 'config/assets';
import { getFinishAvailability } from 'utils/foilAvailability';
import {
  getCardImageUrl,
  getValidCondition,
  stagedPrintingFromRow,
  wantArtFinish,
  SORT_OPTIONS,
  VIEW_OPTIONS,
  formatConditions,
  formatFoilPreference,
} from 'utils/wantListHelpers';
import { formatLanguages } from 'constants/languages';
import StagedCardItem from '../../../Send/components/AddCardsToTradeModal/StagedCardItem';
import '../../../Send/components/AddCardsToTradeModal/AddCardsToTradeModal.css';
import './ComposeTradeModal.css';
import { NoPrice, NO_PRICE_BLOCKS_TRADE, NO_PRICE_TOOLTIP } from 'components/NoPrice/NoPrice';
import { fillFromError, fillFromSlots, firstRealPrice, hasRealPrice, readFilledPrice, sumRealPrices } from 'utils/price';
import { defaultStagedFinish, finishFields, getAvailableFinishes, stagedFinishSource, haveFinishFromRow } from 'utils/finishes';
import { MOBILE_BREAKPOINT, useIsMobile } from 'hooks/useIsMobile';

/**
 * Below this the modal is a full-screen sheet with a bottom tray
 * (Asana 1219036673354850). The project's md breakpoint; ComposeTradeModal.css
 * repeats the number in its media query because CSS cannot import it.
 */
export const COMPOSE_MOBILE_BREAKPOINT = MOBILE_BREAKPOINT;

export interface ComposeTradeRecipient {
  id: number;
  username: string;
}

interface ComposeTradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The alerter — pre-selected as the trade recipient. */
  recipient: ComposeTradeRecipient;
  /**
   * The alerted card, pre-staged in the "Cards to Add" panel on open. Omitted
   * when the entry point has no resolved language for it: the panel opens
   * empty and the recipient's list below stages from the want-list rows.
   */
  initialStagedCard?: SidebarTradeCard;
}

// Chevron SVG for dropdowns
const ChevronSvg: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    className={`add-cards-dropdown-chevron ${open ? 'open' : ''}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path d="M3 4.5L6 7.5L9 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * ComposeTradeModal — composes a BRAND-NEW trade from a Trade Alert.
 *
 * Layout/UX is modeled on AddCardsToTradeModal (two-panel: recipient want list
 * on the left, staged "Cards to Add" on the right), but:
 *  - the title is "Compose Trade" (this is the only surface that uses it),
 *  - the recipient is pre-set from the alert (no existing trade),
 *  - the alerted card is pre-staged on open, and
 *  - submit creates a new trade via createTradeLock → initiateTrade
 *    (the same flow used by SendPage2 / TradeSidebar), not addCardsToTrade.
 */
export const ComposeTradeModal: React.FC<ComposeTradeModalProps> = ({
  isOpen,
  onClose,
  recipient,
  initialStagedCard,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Staged cards state — seeded with the alerted card.
  const [stagedCards, setStagedCards] = useState<SidebarTradeCard[]>([]);

  // Browsing state
  const [page, setPage] = useState(1);
  const [previousPages, setPreviousPages] = useState<CollectionList[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<{ sort: string; direction: SortOrder }>({
    sort: 'created_at',
    direction: 'desc',
  });
  const [viewMode, setViewMode] = useState<'images' | 'list'>('images');

  // Toggle states
  const [autoMatchEnabled, setAutoMatchEnabled] = useState(false);
  const [showAffordableOnly, setShowAffordableOnly] = useState(false);

  // Dropdown states
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<HTMLDivElement>(null);

  // Price calculation
  // null = calculate-price answered and the card has no price; missing = not answered yet
  const [priceMap, setPriceMap] = useState<Record<number, number | null>>({});
  const [calculatePrice] = useCalculatePriceMutation();
  const fetchedIdsRef = useRef<Set<number>>(new Set());

  // API hooks — new-trade flow
  const [createTradeLock] = useCreateTradeLockMutation();
  const [releaseTradeLock] = useReleaseTradeLockMutation();
  const [initiateTrade] = useInitiateTradeMutation();
  const [isConfirming, setIsConfirming] = useState(false);
  const [calculatingIds, setCalculatingIds] = useState<Set<string>>(new Set());

  const handleCalculatingChange = useCallback((id: string, isCalc: boolean) => {
    setCalculatingIds(prev => {
      const next = new Set(prev);
      isCalc ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);

  const anyCalculating = calculatingIds.size > 0;

  // Phones get the full-screen sheet: browse grid plus a pinned bottom tray
  // that expands into the review panel. The old drawer opened over the grid
  // behind a scrim whose sidebar the shared mobile CSS hid, so the scrim ate
  // every tap (Asana 1219036673354850).
  const isMobile = useIsMobile(COMPOSE_MOBILE_BREAKPOINT);
  const [trayExpanded, setTrayExpanded] = useState(false);
  // The sort / view chips open their option list as a fixed menu under the
  // chip: the chip row scrolls sideways, so an absolutely positioned menu
  // inside it would be clipped.
  const [chipMenuTop, setChipMenuTop] = useState(0);

  // Receiver balance
  const { data: receiverProfile } = useGetPublicProfileQuery(recipient.username, {
    skip: !isOpen,
  });

  // Seed the staged panel with the alerted card whenever the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStagedCards(initialStagedCard ? [initialStagedCard] : []);
      setTrayExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialStagedCard?.id]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination on sort/search change
  useEffect(() => {
    setPage(1);
    setPreviousPages([]);
    setPriceMap({});
    fetchedIdsRef.current.clear();
  }, [debouncedSearchTerm, sortOrder.sort, sortOrder.direction, autoMatchEnabled, showAffordableOnly]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) setIsSortOpen(false);
      if (viewRef.current && !viewRef.current.contains(event.target as Node)) setIsViewOpen(false);
    };
    if (isSortOpen || isViewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSortOpen, isViewOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Affordable Only: filter by receiver's balance
  const affordableMaxPrice = showAffordableOnly && receiverProfile?.points != null
    ? receiverProfile.points
    : undefined;

  // Fetch want list
  const { data, isLoading, isFetching } = useFetchPublicWantListQuery({
    username: recipient.username,
    page,
    limit: 48,
    sort: sortOrder.sort,
    direction: sortOrder.direction,
    search: debouncedSearchTerm || undefined,
    autoMatch: autoMatchEnabled || undefined,
    maxPrice: affordableMaxPrice,
  }, { skip: !isOpen });

  // Fetch prices for items without displayPrice
  useEffect(() => {
    if (!data?.wantList || data.wantList.length === 0) return;
    const unpriced = data.wantList.filter(
      item => item.displayPrice == null && !fetchedIdsRef.current.has(item.id)
    );
    unpriced.forEach(item => fetchedIdsRef.current.add(item.id));
    // A row with no sendable version is "No price" without asking calculate-price.
    const unsendable = unpriced.filter(item => !stagedPrintingFromRow(item, item.card?.id));
    if (unsendable.length > 0) {
      setPriceMap(prev => ({ ...prev, ...Object.fromEntries(unsendable.map(item => [item.id, null])) }));
    }
    const itemsNeedingPrice = unpriced.flatMap(item => {
      const staged = stagedPrintingFromRow(item, item.card?.id);
      return staged ? [{ item, staged }] : [];
    });
    if (itemsNeedingPrice.length === 0) return;

    const fetchPrices = async () => {
      try {
        const result = await calculatePrice({
          cards: itemsNeedingPrice.map(({ item, staged }) => {
            // Resolve set details to determine physical foil availability
            let setDetails = item.acceptableSetDetails;
            if (!setDetails || setDetails.length === 0) {
              setDetails = item.acceptableSets?.map((setName, idx) => ({
                name: setName,
                code: item.acceptableSetCodes?.[idx] || setName.toLowerCase().replace(/\s+/g, ''),
                cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
              }));
            }
            const matchingSetDetail = setDetails?.find(
              s => s.cardId === item.card?.id
            ) || setDetails?.[0];

            const defaultFinish = defaultStagedFinish(stagedFinishSource(matchingSetDetail, item.card), item);

            return {
              wantListId: item.id,
              cardId: staged.cardId?.toString(),
              condition: getValidCondition(item.acceptableConditions?.[0]),
              language: staged.language,
              ...finishFields(defaultFinish),
              set: item.card?.setName,
            };
          })
        }).unwrap();
        setPriceMap(prev => ({ ...prev, ...fillFromSlots(result.cards) }));
      } catch (error) {
        console.error('Failed to calculate prices:', error);
        // A lone no-price card is refused with a coded 400: that row has no
        // price. Any other failure leaves the rows pending, not "No price".
        setPriceMap(prev => ({ ...prev, ...fillFromError(error, itemsNeedingPrice.map(({ item }) => item.id)) }));
      }
    };
    fetchPrices();
  }, [data?.wantList, calculatePrice]);

  // Combine pages
  const allItems = useMemo(
    () => [...previousPages, ...(data?.wantList || [])],
    [previousPages, data?.wantList]
  );

  const handleLoadMore = () => {
    if (data?.wantList) {
      setPreviousPages(prev => [...prev, ...data.wantList]);
    }
    setPage(p => p + 1);
  };

  // Count copies of a want-list row currently staged in this session.
  const getStagedCount = useCallback((wantListId: number) => {
    return stagedCards.filter(c => c.wantListId === wantListId).length;
  }, [stagedCards]);

  // Add card to staged
  const handleAddToPackage = useCallback((item: CollectionList) => {
    // A printing with no price cannot be staged (the tile tap reaches here too)
    if (readFilledPrice(item.displayPrice, priceMap, item.id).hasNoPrice) return;
    const cardName = item.card?.name || item.cardName || 'Unknown Card';
    const imageUrl = getCardImageUrl(item);
    const defaultCondition = getValidCondition(item.acceptableConditions?.[0]);

    // Resolve set details FIRST so we can derive foil availability from them
    let acceptableSetDetails: SetDetail[] | undefined = item.acceptableSetDetails;
    if (!acceptableSetDetails || acceptableSetDetails.length === 0) {
      acceptableSetDetails = item.acceptableSets?.map((setName, idx) => ({
        name: setName,
        code: item.acceptableSetCodes?.[idx] || setName.toLowerCase().replace(/\s+/g, ''),
        cardId: item.acceptedPrintingIds?.[idx] || item.card?.id || 0,
      }));
    }

    // Find the set detail matching item.card so dropdown aligns with displayed image/price
    const matchingSetDetail = acceptableSetDetails?.find(
      s => s.cardId === item.card?.id
    ) || acceptableSetDetails?.[0];

    // Determine foil availability and default using SetDetail → price data → card.foil fallback
    const acceptFoil = item.acceptFoil ?? true;
    const acceptNonFoil = item.acceptNonFoil ?? true;
    const { availableInFoil, availableInNonFoil } = getFinishAvailability(
      matchingSetDetail, item.card, acceptFoil, acceptNonFoil,
    );
    // Three-value default (Track D): the first finish the printing has and the want accepts
    const finishSource = stagedFinishSource(matchingSetDetail, item.card);
    const defaultFinish = defaultStagedFinish(finishSource, item);

    // Language and printing come from the row; no sendable version, nothing staged.
    const staged = stagedPrintingFromRow(item, matchingSetDetail?.cardId || item.card?.id);
    if (!staged) return;

    const id = `staged-${item.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newCard: SidebarTradeCard = {
      id,
      wantListId: item.id,
      cardId: staged.cardId,
      cardName,
      set: matchingSetDetail?.name || item.card?.setName || 'Unknown Set',
      setCode: matchingSetDetail?.code || item.card?.setCode || '',
      condition: defaultCondition,
      language: staged.language,
      acceptableLanguages: item.acceptableLanguages,
      ...finishFields(defaultFinish),
      imageUrl: staged.imageUrl ?? imageUrl,
      // Null while unknown; the staged row re-prices itself and Send stays
      // disabled until every row holds a real price.
      pointValue: firstRealPrice(item.displayPrice, priceMap[item.id]),
      acceptableSetDetails,
      acceptableConditions: item.acceptableConditions,
      acceptFoil,
      acceptNonFoil,
      acceptEtched: item.acceptEtched,
      availableInFoil,
      availableInNonFoil,
      availableFinishes: getAvailableFinishes(finishSource),
      scryfallId: staged.scryfallId || matchingSetDetail?.scryfallId || item.card?.scryfallID,
      availableLanguages: item.card?.availableLanguages,
      maxQuantity: item.quantity,
    };

    setStagedCards(prev => [...prev, newCard]);
  }, [priceMap]);

  const handleRemoveStagedCard = useCallback((id: string) => {
    setStagedCards(prev => prev.filter(c => c.id !== id));
  }, []);

  // Phone tiles toggle: a tap on an unselected tile stages one copy, a tap on a
  // selected tile drops every staged copy of that want row (one gesture, one
  // deterministic result; quantities are adjusted in the review panel).
  const handleToggleTile = useCallback((item: CollectionList) => {
    if (getStagedCount(item.id) > 0) {
      setStagedCards(prev => prev.filter(c => c.wantListId !== item.id));
    } else {
      handleAddToPackage(item);
    }
  }, [getStagedCount, handleAddToPackage]);

  const handleUpdateStagedCard = useCallback((id: string, updates: Partial<SidebarTradeCard>) => {
    setStagedCards(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  }, []);

  // Totals
  const stagedTotalPoints = useMemo(
    () => sumRealPrices(stagedCards.map(card => card.pointValue)),
    [stagedCards]
  );

  const receiverBalance = receiverProfile?.points ?? 0;
  const receiverRemainingBalance = receiverBalance - stagedTotalPoints;
  const isOverBudget = stagedTotalPoints > 0 && receiverRemainingBalance < 0;

  // Sort handlers
  const currentSortValue = `${sortOrder.sort}-${sortOrder.direction}`;
  const currentSortLabel = SORT_OPTIONS.find(o => o.value === currentSortValue)?.label || 'Date Added';

  const handleSortSelect = useCallback((compoundValue: string) => {
    const [sort, direction] = compoundValue.split('-');
    setSortOrder({ sort, direction: direction as SortOrder });
    setIsSortOpen(false);
  }, []);

  const handleViewModeChange = useCallback((mode: 'images' | 'list') => {
    setViewMode(mode);
    setIsViewOpen(false);
  }, []);

  // Confirm handler — create a brand-new trade (lock → initiate).
  const handleConfirm = async () => {
    if (stagedCards.length === 0 || stagedCards.some(card => !hasRealPrice(card.pointValue))) return;
    setIsConfirming(true);
    let acquiredLockToken: string | null = null;

    try {
      // Step 1: Acquire trade lock
      const lockResult = await createTradeLock({ receiverId: recipient.id }).unwrap();

      if (!lockResult.success || !lockResult.lockToken) {
        toast.error(lockResult.message || 'Failed to acquire trade lock. Please try again.');
        setIsConfirming(false);
        return;
      }

      acquiredLockToken = lockResult.lockToken;

      // Step 2: Initiate the trade
      const tradeResult = await initiateTrade({
        receiverId: recipient.id,
        lockToken: acquiredLockToken,
        source: 'trade-alerts',
        cards: stagedCards.map(card => ({
          wantListId: card.wantListId,
          cardId: card.cardId?.toString(),
          quantity: 1,
          condition: card.condition,
          language: card.language,
          ...finishFields(haveFinishFromRow(card)),
          set: card.set,
        })),
      }).unwrap();

      if (tradeResult.success) {
        toast.success('Trade created successfully!');
        // Invalidate the receiver's want list cache so traded cards drop off
        dispatch(publicCollectionApi.util.invalidateTags([
          { type: 'PublicWantList', id: recipient.username },
        ]));
        onClose();
        navigate('/sending');
      } else {
        toast.error(tradeResult.message || 'Failed to create trade. Please try again.');
        if (acquiredLockToken) {
          await releaseTradeLock({ receiverId: recipient.id, lockToken: acquiredLockToken });
        }
      }
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to create trade. Please try again.';
      toast.error(errorMessage);
      if (acquiredLockToken) {
        try {
          await releaseTradeLock({ receiverId: recipient.id, lockToken: acquiredLockToken });
        } catch (releaseError) {
          console.error('Failed to release lock:', releaseError);
        }
      }
    } finally {
      setIsConfirming(false);
    }
  };

  const hasMore = data?.hasMore || false;
  const isSearchPending = searchTerm !== debouncedSearchTerm;
  const isEmpty = allItems.length === 0;
  const showSearchEmptyState = isEmpty && debouncedSearchTerm && !isSearchPending && !isFetching;
  const showGenericEmptyState = isEmpty && !debouncedSearchTerm && !isSearchPending && !isFetching;
  const showLoadingState = isEmpty && (isSearchPending || isFetching);

  // List view table data
  const tableCellsData: CardsTableCellsData = useMemo(() => {
    if (viewMode !== 'list') return { headCells: [], bodyRows: [] };

    const headCells: { key: string; label: string; columnWidth?: number; noSort?: boolean }[] = [
      { key: 'card_name', label: 'Name' },
      { key: 'setName', label: 'Set', columnWidth: 65, noSort: true },
    ];

    if (!isMobile) {
      headCells.push(
        { key: 'condition', label: 'Cond', columnWidth: 40, noSort: true },
        { key: 'lang', label: 'Lang', columnWidth: 35, noSort: true },
        { key: 'isFoil', label: 'Foil', columnWidth: 35, noSort: true },
        { key: 'reference_price', label: 'Price', columnWidth: 70, noSort: true },
      );
    }

    headCells.push({ key: 'quantity', label: 'Qty', columnWidth: 35, noSort: true });
    headCells.push({ key: 'action', label: '', columnWidth: 115, noSort: true });

    return {
      headCells,
      bodyRows: allItems.map((row) => {
        const rowPrice = readFilledPrice(row.displayPrice, priceMap, row.id);
        const stagedCount = getStagedCount(row.id);
        const remaining = row.quantity - stagedCount;
        const allAdded = remaining <= 0;

        const components: React.ReactNode[] = [
          // Name with thumbnail
          <div className="collection-card-name" key="name">
            {row.card?.scryfallID && (
              <img
                src={AssetUrls.cards.getCardImage(row.card.scryfallID, 'art_crop')}
                className="collection-card-name-img"
                alt={row.cardName || row.card?.name || 'Card'}
                onError={(e) => {
                  const img = e.currentTarget;
                  img.src = AssetUrls.cards.defaultBack;
                }}
              />
            )}
            <span className="collection-card-name-text">
              {row.cardName || row.card?.name}
            </span>
          </div>,
          // Set
          <div className="collection-card-set" key="set">
            {(!row.acceptableSets || row.acceptableSets.length === 0) ? (
              <span className="collection-card-set-text">Any</span>
            ) : (
              <>
                <SetIcon
                  setIconUrl={
                    row.card?.setIconUrl ||
                    (row.acceptableSetCodes?.[0]
                      ? `${AssetUrls.cardsCDN}/sets/icons/${row.acceptableSetCodes[0]}.svg`
                      : undefined)
                  }
                  setName={row.acceptableSets[0]}
                  inverted={false}
                  size="sm"
                />
                <span className="collection-card-set-code">
                  {(row.acceptableSetCodes?.[0] || row.card?.setCode || '').toUpperCase()}
                </span>
                {row.acceptableSets.length > 1 && (
                  <span className="collection-card-set-more" title={row.acceptableSets.join(', ')}>
                    +{row.acceptableSets.length - 1}
                  </span>
                )}
              </>
            )}
          </div>,
        ];

        if (!isMobile) {
          components.push(
            <div key="cond">{formatConditions(row.acceptableConditions)}</div>,
            <div key="lang">{formatLanguages(row.acceptableLanguages)}</div>,
            <div key="foil">{formatFoilPreference(row)}</div>,
            <div key="price">
              {rowPrice.price !== null ? <Points points={rowPrice.price} type="small" /> : rowPrice.pending ? null : <NoPrice />}
            </div>,
          );
        }

        // Quantity
        components.push(
          <div className="collection-card-quantity-container" key="qty">
            {allAdded ? (
              <span className="add-cards-list-qty-done">&#10003;</span>
            ) : (
              <span>{row.quantity}</span>
            )}
          </div>,
        );

        // Action
        components.push(
          <div key="action">
            {allAdded ? (
              <span className="add-cards-list-added">Added</span>
            ) : (
              <button
                className="add-cards-list-add-btn"
                disabled={rowPrice.hasNoPrice}
                title={rowPrice.hasNoPrice ? NO_PRICE_TOOLTIP : undefined}
                onClick={(e) => { e.stopPropagation(); handleAddToPackage(row); }}
              >
                Add to Package
              </button>
            )}
          </div>
        );

        return {
          key: row.id.toString(),
          card: row.card || ({} as any),
          components,
          onClick: () => {
            if (row.card) {
              navigate(buildCardUrl(row.card));
            }
          },
        };
      }),
    };
  }, [viewMode, allItems, isMobile, priceMap, getStagedCount, handleAddToPackage, navigate]);

  // Grid card item renderer
  const renderGridCard = (item: CollectionList) => {
    const cardName = item.card?.name || item.cardName || 'Unknown Card';
    const imageUrl = getCardImageUrl(item);
    const defaultImage = `${AssetUrls.base}/assets/images/card-back.png`;
    const stagedCount = getStagedCount(item.id);
    const remaining = item.quantity - stagedCount;
    const allAdded = remaining <= 0;

    // Phone: the whole tile is the control (tap toggles), with a selected ring
    // and check badge; the hover overlay is hidden by the sheet CSS.
    const selected = stagedCount > 0;
    const itemPrice = readFilledPrice(item.displayPrice, priceMap, item.id);
    const tileProps = isMobile
      ? {
          role: 'button' as const,
          tabIndex: 0,
          'aria-pressed': selected,
          'aria-label': `${selected ? 'Remove' : 'Add'} ${cardName}`,
          onClick: () => handleToggleTile(item),
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleTile(item);
            }
          },
        }
      : {};

    return (
      <div
        key={item.id}
        className={`add-cards-grid-card${isMobile && selected ? ' is-selected' : ''}`}
        data-testid="compose-grid-card"
        data-selected={isMobile ? selected : undefined}
        {...tileProps}
      >
        <div className="add-cards-grid-card-image-container">
          <FoilCardImage
            src={imageUrl}
            alt={cardName}
            className="add-cards-grid-card-image"
            finish={wantArtFinish(item)}
            onError={(e: any) => { e.currentTarget.src = defaultImage; }}
            loading="lazy"
            borderRadius="8px"
          />

          {/* Quantity badge */}
          {item.quantity > 1 && (
            <div className="add-cards-grid-card-quantity">x{item.quantity}</div>
          )}

          {isMobile && selected && (
            <span className="compose-tile-check" aria-hidden="true">&#10003;</span>
          )}

          {/* Overlay */}
          <div className="add-cards-grid-card-overlay">
            <span className="add-cards-grid-card-overlay-title">{cardName}</span>
            {remaining > 1 && (
              <div className="add-cards-grid-card-overlay-remaining">{remaining} left</div>
            )}
            {allAdded ? (
              <div className="add-cards-grid-card-overlay-added">
                <span>&#10003;</span>
                <span>Added</span>
              </div>
            ) : (
              <button
                className="add-cards-grid-card-overlay-btn"
                disabled={itemPrice.hasNoPrice}
                title={itemPrice.hasNoPrice ? NO_PRICE_TOOLTIP : undefined}
                onClick={(e) => { e.stopPropagation(); handleAddToPackage(item); }}
              >
                <span>Add to Package</span>
                <img src={AssetUrls.icons.navigation.sending} alt="" className="add-cards-grid-card-overlay-btn-icon" />
              </button>
            )}
          </div>
        </div>
        <div className="add-cards-grid-card-info">
          {itemPrice.price !== null ? <Points points={itemPrice.price} type="small" /> : itemPrice.pending ? null : <NoPrice />}
        </div>
      </div>
    );
  };

  // The staged rows, the totals and the Send button are shared by the desktop
  // sidebar and the phone tray's review panel, so the two cannot drift.
  const stagedRows = stagedCards.map(card => (
    <StagedCardItem
      key={card.id}
      card={card}
      onUpdate={handleUpdateStagedCard}
      onRemove={handleRemoveStagedCard}
      onCalculatingChange={handleCalculatingChange}
    />
  ));

  // Points render through the brand component at every breakpoint; the
  // balance line used to print "{n} pts" (Decision 10).
  const renderSummary = () => (
    <>
      <div className="add-cards-sidebar-summary">
        <div className="add-cards-sidebar-summary-row">
          <span>You will earn:</span>
          <Points points={stagedTotalPoints} type="small" />
        </div>
        {stagedCards.length > 0 && (
          <div className={`add-cards-sidebar-summary-row ${isOverBudget ? 'over-budget' : ''}`} data-testid="receiver-balance-after">
            <span>Receiver balance after:</span>
            <Points type="inline" points={receiverRemainingBalance} isOver={isOverBudget} />
          </div>
        )}
      </div>

      {isOverBudget && (
        <BalanceWarning
          username={recipient.username}
          pointsShort={Math.abs(receiverRemainingBalance)}
        />
      )}
    </>
  );

  const sendLabel = isConfirming ? 'Creating...' : `Send ${stagedCards.length} Card${stagedCards.length !== 1 ? 's' : ''}`;
  // A staged card with no price cannot be sent: the backend refuses it
  const hasNoPriceCard = stagedCards.some(card => !hasRealPrice(card.pointValue));
  const sendDisabled = stagedCards.length === 0 || isOverBudget || isConfirming || anyCalculating || hasNoPriceCard;

  // Sidebar content (desktop)
  const renderSidebar = () => (
    <div className="add-cards-sidebar">
      <div className="add-cards-sidebar-header">
        <span className="add-cards-sidebar-title">
          Cards to Add ({stagedCards.length})
        </span>
        {stagedCards.length > 0 && (
          <Points points={stagedTotalPoints} type="small" />
        )}
      </div>

      <div className="add-cards-sidebar-cards">
        {stagedCards.length === 0 ? (
          <div className="add-cards-sidebar-empty">
            <p>Browse the want list and click "Add to Package" to stage cards.</p>
          </div>
        ) : (
          stagedRows
        )}
      </div>

      <div className="add-cards-sidebar-footer">
        {renderSummary()}

        {hasNoPriceCard && !anyCalculating && (
          <p className="no-price-blocks-trade" role="status" data-testid="no-price-blocks-trade">
            {NO_PRICE_BLOCKS_TRADE}
          </p>
        )}

        <div className="add-cards-sidebar-actions">
          <MagicButton
            label="Cancel"
            color="dark-grey"
            onPress={onClose}
            isDisabled={isConfirming}
          />
          <MagicButton
            label={sendLabel}
            color="purple"
            onPress={handleConfirm}
            isDisabled={sendDisabled}
            isLoading={isConfirming}
          />
        </div>
      </div>
    </div>
  );

  // The phone tray: pinned to the bottom of the sheet, collapsed (hint or
  // count + total + Review) or expanded into the review panel (Decisions 8
  // and 9, colours per the spec addendum).
  const cardWord = stagedCards.length === 1 ? 'card' : 'cards';
  const renderTray = () => (
    <div className={`compose-tray${trayExpanded ? ' is-expanded' : ''}`} data-testid="compose-tray" data-expanded={trayExpanded}>
      {!trayExpanded ? (
        stagedCards.length === 0 ? (
          <p className="compose-tray__hint">Tap cards to add them to this trade</p>
        ) : (
          <div className="compose-tray__summary">
            <div className="compose-tray__text">
              <span className="compose-tray__count">{stagedCards.length} {cardWord} added</span>
              <span className="compose-tray__earn">
                You earn <Points type="inline" points={stagedTotalPoints} />
              </span>
            </div>
            <button type="button" className="compose-tray__review" onClick={() => setTrayExpanded(true)}>
              Review
            </button>
          </div>
        )
      ) : (
        <>
          <div className="compose-tray__header">
            <span className="add-cards-sidebar-title">Cards to add ({stagedCards.length})</span>
            <Points points={stagedTotalPoints} type="small" />
          </div>

          <div className="compose-tray__rows">
            {stagedCards.length === 0 ? (
              <p className="compose-tray__hint">Tap cards to add them to this trade</p>
            ) : (
              stagedRows
            )}
          </div>

          <div className="compose-tray__footer">
            {renderSummary()}
            {hasNoPriceCard && !anyCalculating && (
              <p className="no-price-blocks-trade" role="status" data-testid="no-price-blocks-trade">
                {NO_PRICE_BLOCKS_TRADE}
              </p>
            )}
            <div className="compose-tray__actions">
              <button type="button" className="compose-tray__back" onClick={() => setTrayExpanded(false)}>
                Back to cards
              </button>
              <MagicButton
                label={sendLabel}
                color="purple"
                onPress={handleConfirm}
                isDisabled={sendDisabled}
                isLoading={isConfirming}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  // The phone chip row: the same four controls and the same state as the
  // desktop bar, as 36px pills in one sideways-scrolling row. Sort and View
  // open the existing option lists as a fixed menu under the row.
  const openChipMenu = (which: 'sort' | 'view', chip: HTMLElement) => {
    setChipMenuTop(chip.getBoundingClientRect().bottom + 4);
    if (which === 'sort') {
      setIsSortOpen(open => !open);
      setIsViewOpen(false);
    } else {
      setIsViewOpen(open => !open);
      setIsSortOpen(false);
    }
  };
  const renderChips = () => (
    <div className="compose-chips" role="toolbar" aria-label="Filters" data-testid="compose-chips">
      <button
        type="button"
        className={`compose-chip${autoMatchEnabled ? ' is-on' : ''}`}
        aria-pressed={autoMatchEnabled}
        onClick={() => setAutoMatchEnabled(v => !v)}
      >
        <span className="add-cards-auto-match-icon" aria-hidden="true">&#9889;</span>
        Auto-Match
      </button>
      <button
        type="button"
        className={`compose-chip${showAffordableOnly ? ' is-on' : ''}`}
        aria-pressed={showAffordableOnly}
        onClick={() => setShowAffordableOnly(v => !v)}
      >
        Affordable
      </button>
      <div className="compose-chip-menu" ref={sortRef}>
        <button
          type="button"
          className={`compose-chip${isSortOpen ? ' is-open' : ''}`}
          aria-haspopup="listbox"
          aria-expanded={isSortOpen}
          onClick={(e) => openChipMenu('sort', e.currentTarget)}
        >
          <img src={AssetUrls.icons.controls.sort} alt="" className="add-cards-dropdown-icon" />
          {currentSortLabel}
          <ChevronSvg open={isSortOpen} />
        </button>
        {isSortOpen && (
          <div className="add-cards-dropdown-menu compose-chip-options" role="listbox" style={{ top: chipMenuTop }}>
            {SORT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`add-cards-dropdown-option ${currentSortValue === option.value ? 'selected' : ''}`}
                onClick={() => handleSortSelect(option.value)}
              >
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="compose-chip-menu" ref={viewRef}>
        <button
          type="button"
          className={`compose-chip${isViewOpen ? ' is-open' : ''}`}
          aria-haspopup="listbox"
          aria-expanded={isViewOpen}
          onClick={(e) => openChipMenu('view', e.currentTarget)}
        >
          <img src={VIEW_OPTIONS.find(o => o.value === viewMode)?.icon} alt="" className="add-cards-dropdown-icon" />
          {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
          <ChevronSvg open={isViewOpen} />
        </button>
        {isViewOpen && (
          <div className="add-cards-dropdown-menu compose-chip-options" role="listbox" style={{ top: chipMenuTop }}>
            {VIEW_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`add-cards-dropdown-option ${viewMode === option.value ? 'selected' : ''}`}
                onClick={() => handleViewModeChange(option.value)}
              >
                <img src={option.icon} alt="" className="add-cards-dropdown-option-icon" />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!isOpen) return null;

  const modalContent = (
    <div className={`add-cards-modal-overlay compose-trade-modal${isMobile ? ' is-sheet' : ''}`} onClick={onClose}>
      <div className="add-cards-modal compose-trade-modal__sheet" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-cards-modal-header">
          <div className="add-cards-modal-header-left">
            <h2 className="add-cards-modal-title">
              Compose Trade
            </h2>
            <span className="add-cards-modal-subtitle">
              Sending to {recipient.username}
            </span>
          </div>
          <button className="add-cards-modal-close" onClick={onClose} aria-label="Close">
            <img src={AssetUrls.icons.dropdowns.close} alt="" className="add-cards-modal-close-icon" />
          </button>
        </div>

        {/* Controls bar */}
        <div className="add-cards-controls">
          <div className="add-cards-search">
            <TextInputField
              leftComponent={<FontAwesomeIcon icon="magnifying-glass" color="#DD5BEA" size="sm" />}
              value={searchTerm}
              placeholder="Card Name"
              onChange={setSearchTerm}
            />
          </div>

          {isMobile && renderChips()}

          {/* Auto-Match Toggle */}
          {!isMobile && (
          <div className="add-cards-auto-match-toggle">
            <label className="add-cards-toggle-label">
              <span className="add-cards-auto-match-icon">&#9889;</span>
              <span className="add-cards-toggle-text">Auto-Match</span>
              <div className="add-cards-toggle-switch">
                <input
                  type="checkbox"
                  checked={autoMatchEnabled}
                  onChange={(e) => setAutoMatchEnabled(e.target.checked)}
                />
                <span className="add-cards-toggle-slider" />
              </div>
            </label>
          </div>
          )}

          {/* Affordable Only Toggle */}
          {!isMobile && (
          <div className="add-cards-affordable-toggle">
            <label className="add-cards-toggle-label">
              <span className="add-cards-toggle-text">Affordable Only</span>
              <div className="add-cards-toggle-switch">
                <input
                  type="checkbox"
                  checked={showAffordableOnly}
                  onChange={(e) => setShowAffordableOnly(e.target.checked)}
                />
                <span className="add-cards-toggle-slider" />
              </div>
            </label>
          </div>
          )}

          {!isMobile && (
          <div className="add-cards-view-selector" ref={viewRef}>
            <button
              type="button"
              className={`add-cards-dropdown-trigger ${isViewOpen ? 'open' : ''}`}
              onClick={() => setIsViewOpen(!isViewOpen)}
            >
              <img
                src={VIEW_OPTIONS.find(o => o.value === viewMode)?.icon}
                alt=""
                className="add-cards-dropdown-icon"
              />
              <span className="add-cards-dropdown-text">
                {VIEW_OPTIONS.find(o => o.value === viewMode)?.label}
              </span>
              <ChevronSvg open={isViewOpen} />
            </button>
            {isViewOpen && (
              <div className="add-cards-dropdown-menu" role="listbox">
                {VIEW_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-dropdown-option ${viewMode === option.value ? 'selected' : ''}`}
                    onClick={() => handleViewModeChange(option.value)}
                  >
                    <img src={option.icon} alt="" className="add-cards-dropdown-option-icon" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {!isMobile && (
          <div className="add-cards-sort-selector" ref={sortRef}>
            <button
              type="button"
              className={`add-cards-dropdown-trigger ${isSortOpen ? 'open' : ''}`}
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <img src={AssetUrls.icons.controls.sort} alt="" className="add-cards-dropdown-icon" />
              <span className="add-cards-dropdown-text">{currentSortLabel}</span>
              <ChevronSvg open={isSortOpen} />
            </button>
            {isSortOpen && (
              <div className="add-cards-dropdown-menu" role="listbox">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`add-cards-dropdown-option ${currentSortValue === option.value ? 'selected' : ''}`}
                    onClick={() => handleSortSelect(option.value)}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Main content area */}
        <div className="add-cards-content">
          {/* Want list browsing area */}
          <div className="add-cards-want-list">
            {isLoading && (
              <div className="add-cards-grid">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="add-cards-grid-card">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {showLoadingState && !isLoading && (
              <div className="add-cards-grid">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="add-cards-grid-card">
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.1)', aspectRatio: '488 / 680', borderRadius: '8px' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {showSearchEmptyState && (
              <div className="add-cards-empty">
                <p>No cards matching "{debouncedSearchTerm}"</p>
              </div>
            )}

            {showGenericEmptyState && (
              <div className="add-cards-empty">
                <p>{recipient.username}'s want list is empty.</p>
              </div>
            )}

            {!isEmpty && viewMode === 'images' && (
              <div className="add-cards-grid">
                {allItems.map(renderGridCard)}
              </div>
            )}

            {!isEmpty && viewMode === 'list' && (
              <div className="add-cards-list-view">
                <CardsTable
                  isLoading={false}
                  cellsData={tableCellsData}
                  orderBy={sortOrder.sort}
                  order={sortOrder.direction}
                />
              </div>
            )}

            {hasMore && !isEmpty && (
              <div className="add-cards-load-more">
                <MagicButton
                  label={isFetching ? 'Loading...' : 'Load More'}
                  color="dark-grey"
                  onPress={handleLoadMore}
                  isLoading={isFetching}
                  isDisabled={isFetching}
                />
              </div>
            )}
          </div>

          {/* Sidebar (desktop) */}
          {!isMobile && renderSidebar()}
        </div>

        {/* Phone: the pinned tray replaces the sidebar (never a drawer over a scrim) */}
        {isMobile && renderTray()}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ComposeTradeModal;
